import React, {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { Grid } from "@mui/system";
import { enqueueSnackbar } from "notistack";
import { useExpansionList, useProductList, useProductUpdate } from "hooks";
import {
  LanguageFilterType,
  ProductColumnToUpdate,
  ProductUpdateInput,
  SearchTextParamName,
  UpdateProductInput,
  ValueSubmitTrigger,
  getNextProductColumn,
  getPreviousProductColumn,
} from "types";
import {
  CustomButton,
  CustomNumberFieldHandles,
  CustomTable,
  Loading,
  SortableHeader,
} from "core";
import { EditIcon } from "components";
import { BoxHeadlineContainer } from "layout/Container/BoxHeadlineContainer";
import { ExpansionCardListPdfExport } from "modules/expansions/expansionproducts/ExpansionCardListPdfExport";
import { IconColumnFormatter } from "modules/shared/columnformatters/IconColumnFormatter";
import { WebsiteColumnFormatter } from "modules/shared/columnformatters/WebsiteColumnFormatter";
import { Routes } from "routes";
import { FolderChangeColumn } from "../ColumnFormatters/FolderChangeColumn";
import { ProductNumberColumn } from "../ColumnFormatters/ProductNumberColumn";
import { ProductListExcelExport } from "../ProductListExcelExport";
import { ProductListFilter } from "../ProductListFilter";
import useStyles from "./styles";

const ProductListPage: React.FC = () => {
  const { classes, cx } = useStyles();

  const [searchParams] = useSearchParams();

  const { isLoading, productList: products } = useProductList();
  const { expansionList } = useExpansionList(false);

  // Die im Filter gewählte Edition. "search" steht für "Suche in allen Editionen"
  // und ist keine Edition - dafür gibt es keine druckbare Kartenliste.
  const selectedExpansion = useMemo(() => {
    const expansionIDParam = searchParams.get("expansionID");

    if (!expansionIDParam || expansionIDParam === "search") {
      return null;
    }

    return (
      expansionList.find((expansion) => expansion.id === expansionIDParam) ??
      null
    );
  }, [searchParams, expansionList]);

  const [selectedRow, setSelectedRow] = useState<ProductUpdateInput | null>(
    null,
  );
  const [selectedColumn, setSelectedColumn] =
    useState<ProductColumnToUpdate | null>(null);

  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const [productList, setProductList] = useState<ProductUpdateInput[]>([]);

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const updateMutation = useProductUpdate();

  const loadProductInputs = useCallback(() => {
    const productInputs: ProductUpdateInput[] = products.map((product) => ({
      ...product,
      priceStore:
        product.priceStore === null || product.priceStore === undefined
          ? ""
          : product.priceStore,
      priceStoreInputRef: createRef<CustomNumberFieldHandles>(),
      deStock:
        product.deStock === null || product.deStock === undefined
          ? ""
          : product.deStock,
      deStockInputRef: createRef<CustomNumberFieldHandles>(),
      deTransfer:
        product.deTransfer === null || product.deTransfer === undefined
          ? ""
          : product.deTransfer,
      deTransferInputRef: createRef<CustomNumberFieldHandles>(),
      enStock:
        product.enStock === null || product.enStock === undefined
          ? ""
          : product.enStock,
      enStockInputRef: createRef<CustomNumberFieldHandles>(),
      enTransfer:
        product.enTransfer === null || product.enTransfer === undefined
          ? ""
          : product.enTransfer,
      enTransferInputRef: createRef<CustomNumberFieldHandles>(),
    }));

    setProductList(productInputs);
  }, [products]);

  useEffect(() => {
    loadProductInputs();
  }, [loadProductInputs]);

  const language = useMemo(() => {
    const languageParam = searchParams.get("language");

    if (!languageParam) {
      return LanguageFilterType.All;
    }

    return languageParam as LanguageFilterType;
  }, [searchParams]);

  const expansionID = searchParams.get("expansionID");

  const resetSelection = () => {
    setSelectedRow(null);
    setSelectedColumn(null);
  };

  const handleProductSelection = (
    product: ProductUpdateInput,
    value: number,
    column: ProductColumnToUpdate,
    rowIndex: number,
    trigger: ValueSubmitTrigger,
  ) => {
    const isFirstRow = rowIndex === 0;
    const isLastRow = rowIndex === productList.length - 1;
    const isFirstColumn = column === ProductColumnToUpdate.priceStore;
    const isLastColumn =
      language === LanguageFilterType.DE
        ? column === ProductColumnToUpdate.deTransfer
        : column === ProductColumnToUpdate.enTransfer;

    switch (trigger) {
      case ValueSubmitTrigger.Enter:
        if (isLastRow) {
          resetSelection();
        } else {
          setSelectedRow(productList[rowIndex + 1]);
        }
        break;
      case ValueSubmitTrigger.Blur:
      case ValueSubmitTrigger.Escape:
        resetSelection();
        break;
      case ValueSubmitTrigger.ArrowUp:
        if (!isFirstRow) {
          setSelectedRow(productList[rowIndex - 1]);
        }
        break;
      case ValueSubmitTrigger.ArrowDown:
        if (!isLastRow) {
          setSelectedRow(productList[rowIndex + 1]);
        }
        break;
      case ValueSubmitTrigger.ShiftTab:
      case ValueSubmitTrigger.ArrowLeft:
        if (isFirstColumn && isFirstRow) return;

        if (isFirstColumn) {
          setSelectedRow(productList[rowIndex - 1]);
        }

        setSelectedColumn(getPreviousProductColumn(column, language));

        break;
      case ValueSubmitTrigger.Tab:
      case ValueSubmitTrigger.ArrowRight:
        if (isLastColumn && isLastRow) return;

        if (isLastColumn) {
          setSelectedRow(productList[rowIndex + 1]);
        }

        setSelectedColumn(getNextProductColumn(column, language));

        break;
    }

    if (trigger !== ValueSubmitTrigger.Escape) {
      setProductList((prevList) =>
        prevList.map((item) => {
          if (item.id === product.id) {
            return {
              ...item,
              [column]: value,
            };
          }
          return item;
        }),
      );
    }
  };

  const isProductColumnSelected = (
    product: ProductUpdateInput,
    column: ProductColumnToUpdate,
  ) => selectedRow?.id === product.id && selectedColumn === column;

  const productColumnSelectHandler = (
    product: ProductUpdateInput,
    column: ProductColumnToUpdate,
  ) => {
    setSelectedRow(product);
    setSelectedColumn(column);
  };

  const handleEditModeSubmit = async () => {
    for (const product of productList) {
      if (product.priceStore === "") {
        product.priceStoreInputRef.current?.highlight();
        return enqueueSnackbar(
          "Bitte geben Sie einen Preis für " + product.productDeName + " ein!",
        );
      }

      if (product.deStock === "") {
        product.deStockInputRef.current?.highlight();
        return enqueueSnackbar(
          "Bitte geben Sie einen DE-Bestand für " +
            product.productDeName +
            " ein!",
        );
      }

      if (product.deTransfer === "") {
        product.deTransferInputRef.current?.highlight();
        return enqueueSnackbar(
          "Bitte geben Sie einen DE-Transfer für " +
            product.productDeName +
            " ein!",
        );
      }

      if (product.enStock === "") {
        product.enStockInputRef.current?.highlight();
        return enqueueSnackbar(
          "Bitte geben Sie einen EN-Bestand für " +
            product.productDeName +
            " ein!",
        );
      }

      if (product.enTransfer === "") {
        product.enTransferInputRef.current?.highlight();
        return enqueueSnackbar(
          "Bitte geben Sie einen EN-Transfer für " +
            product.productDeName +
            " ein!",
        );
      }
    }

    setSubmitLoading(true);
    const productListPromises = productList.map((product) => {
      const updateInput: UpdateProductInput = {
        id: product.id,
        expansionID: product.expansionID,
        priceStore: product.priceStore === "" ? 0 : product.priceStore,
        deStock: product.deStock === "" ? 0 : product.deStock,
        deTransfer: product.deTransfer === "" ? 0 : product.deTransfer,
        enStock: product.enStock === "" ? 0 : product.enStock,
        enTransfer: product.enTransfer === "" ? 0 : product.enTransfer,
      };

      return updateMutation.mutateAsync(updateInput);
    });

    await Promise.all(productListPromises);
    setSubmitLoading(false);
    setIsEditMode(false);
    resetSelection();

    enqueueSnackbar("Änderungen erfolgreich gespeichert!", {
      variant: "success",
    });
  };

  return (
    <>
      <BoxHeadlineContainer
        boxTitle="Karten"
        boxIcon={Routes.products.icon}
        boxSubTitle={productList.length.toString()}
        paddingHorizontal={0}
        paddingVertical={0}
        boxAlignment="center"
        boxWidth="lg"
        boxMenu={
          <>
            {isEditMode ? (
              <>
                <CustomButton
                  text="Änderungen speichern"
                  size="small"
                  style="filled"
                  onClick={handleEditModeSubmit}
                  loading={submitLoading}
                  disabled={submitLoading}
                  marginRight={15}
                />
                <CustomButton
                  text="Abbrechen"
                  size="small"
                  style="outlined"
                  color="red"
                  onClick={() => {
                    loadProductInputs();
                    resetSelection();
                    setIsEditMode(false);
                  }}
                  loading={submitLoading}
                  disabled={submitLoading}
                />
              </>
            ) : (
              <CustomButton
                text="Editier-Modus"
                size="small"
                color="green"
                onClick={() => setIsEditMode(true)}
                iconBefore={<EditIcon />}
                disabled={products.length === 0}
              />
            )}
            {/* Nur bei konkret ausgewählter Edition: das PDF ist eine
                Editions-Kartenliste. "search" ist der Sonderwert des Filters für
                "Suche in allen Editionen" und ist deshalb ausgeschlossen. */}
            {selectedExpansion && (
              <ExpansionCardListPdfExport expansion={selectedExpansion} />
            )}
            <ProductListExcelExport productList={products} />
          </>
        }
      >
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid size={12}>
            <ProductListFilter />
          </Grid>
        </Grid>

        <CustomTable
          marginBottom={0}
          uppercaseHeaders={false}
          fixedLayout={productList.length !== 0}
        >
          {language === LanguageFilterType.All ? (
            <colgroup>
              <col width="10px" />
              <col width="10px" />
              <col width="100px" />
              <col width="100px" />
              <col width="10px" />
              <col width="10px" />
              <col width="15px" />
              <col width="15px" />
              <col width="15px" />
              <col width="15px" />
              <col width="15px" />
              <col width="15px" />
              <col width="15px" />
            </colgroup>
          ) : (
            <colgroup>
              <col width="10px" />
              <col width="10px" />
              <col width="300px" />
              <col width="10px" />
              <col width="15px" />
              <col width="15px" />
              <col width="15px" />
              <col width="15px" />
            </colgroup>
          )}

          <thead>
            {language === LanguageFilterType.All ? (
              <tr>
                <th colSpan={5}></th>
                <th colSpan={6}>Bestand Laden</th>
                <th colSpan={2}>Bestand MKM</th>
              </tr>
            ) : (
              <tr>
                <th colSpan={4}></th>
                <th colSpan={3}>Bestand Laden</th>
                <th>MKM</th>
              </tr>
            )}
            <tr>
              <SortableHeader
                headerID="website"
                header="CM"
                dataLength={productList.length}
                isLoadingDataList={isLoading}
              />
              <SortableHeader
                headerID="rarity"
                header="R"
                dataLength={productList.length}
                isLoadingDataList={isLoading}
              />
              {language !== LanguageFilterType.EN && (
                <SortableHeader
                  headerID="productDeName"
                  header="DE-Name"
                  isDefaultSort={true}
                  defaultSortOrder="asc"
                  dataLength={productList.length}
                  isLoadingDataList={isLoading}
                />
              )}
              {language !== LanguageFilterType.DE && (
                <SortableHeader
                  headerID="productEnName"
                  header="EN-Name"
                  dataLength={productList.length}
                  isLoadingDataList={isLoading}
                />
              )}
              <SortableHeader
                headerID="icon"
                header="Exp"
                dataLength={productList.length}
                isLoadingDataList={isLoading}
              />
              {language === LanguageFilterType.All && (
                <SortableHeader
                  headerID="folderChange"
                  header="Ord"
                  dataLength={productList.length}
                  isLoadingDataList={isLoading}
                />
              )}
              <SortableHeader
                headerID="priceStore"
                header="Preis"
                dataLength={productList.length}
                isLoadingDataList={isLoading}
              />
              {language !== LanguageFilterType.EN && (
                <>
                  <SortableHeader
                    headerID="deStock"
                    header="DE"
                    dataLength={productList.length}
                    isLoadingDataList={isLoading}
                  />
                  <SortableHeader
                    headerID="deTransfer"
                    header="O:DE"
                    dataLength={productList.length}
                    isLoadingDataList={isLoading}
                  />
                </>
              )}
              {language !== LanguageFilterType.DE && (
                <>
                  <SortableHeader
                    headerID="enStock"
                    header="EN"
                    dataLength={productList.length}
                    isLoadingDataList={isLoading}
                  />
                  <SortableHeader
                    headerID="enTransfer"
                    header="O:EN"
                    dataLength={productList.length}
                    isLoadingDataList={isLoading}
                  />
                </>
              )}

              {language !== LanguageFilterType.EN && (
                <SortableHeader
                  headerID="stockCountDe"
                  header="DE"
                  dataLength={productList.length}
                  isLoadingDataList={isLoading}
                />
              )}

              {language !== LanguageFilterType.DE && (
                <SortableHeader
                  headerID="stockCountEn"
                  header="EN"
                  dataLength={productList.length}
                  isLoadingDataList={isLoading}
                />
              )}
            </tr>
          </thead>
          <tbody>
            {productList.length === 0 ? (
              <tr>
                <td colSpan={99}>
                  <div className={classes.noResults}>
                    {isLoading ? (
                      <Loading
                        size="25px"
                        description={
                          "Bitte warten. " +
                          (!expansionID || expansionID === "search"
                            ? "Suche wird durchgeführt..."
                            : "Karten der Edition werden geladen...")
                        }
                        style={{ margin: 0 }}
                      />
                    ) : expansionID === "search" &&
                      !searchParams.get(SearchTextParamName.SearchProduct) ? (
                      "Bitte geben Sie einen Suchbegriff ein!"
                    ) : expansionID ? (
                      "Keine Produkte gefunden"
                    ) : (
                      "Bitte wählen Sie eine Edition aus oder geben Sie einen Suchbegriff ein!"
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              productList.map((product, rowIndex) => {
                const expansion = expansionList.find(
                  (expansion) => expansion.id === product.expansionID,
                );

                if (!expansion) {
                  return null;
                }

                const rowClass =
                  rowIndex % 2 === 0 ? classes.evenRow : classes.oddRow;

                return (
                  <tr key={product.id} className={classes.productItem}>
                    <td width="10px">
                      <WebsiteColumnFormatter product={product} />
                    </td>
                    <td width="10px">{product.rarity.charAt(0)}</td>
                    {language !== LanguageFilterType.EN && (
                      <td width="200px" className={classes.noWrap}>
                        <div className={classes.productName}>
                          {product.productAlias ?? product.productDeName}
                        </div>
                      </td>
                    )}
                    {language !== LanguageFilterType.DE && (
                      <td width="200px" className={classes.noWrap}>
                        <div className={classes.productName}>
                          {product.productEnName}
                        </div>
                      </td>
                    )}
                    <td width="20px">
                      <IconColumnFormatter expansion={expansion} />
                    </td>
                    {language === LanguageFilterType.All && (
                      <td width="20px" style={{ padding: 0 }}>
                        <FolderChangeColumn product={product} />
                      </td>
                    )}
                    <td
                      width="30px"
                      onClick={() =>
                        productColumnSelectHandler(
                          product,
                          ProductColumnToUpdate.priceStore,
                        )
                      }
                      className={cx(
                        classes.numberColumn,
                        isProductColumnSelected(
                          product,
                          ProductColumnToUpdate.priceStore,
                        )
                          ? classes.numberColumnSelected
                          : "",
                        rowClass,
                      )}
                    >
                      <ProductNumberColumn
                        isEditMode={isEditMode}
                        product={product}
                        productField="priceStore"
                        productColumn={ProductColumnToUpdate.priceStore}
                        rowIndex={rowIndex}
                        selectedRow={selectedRow}
                        selectedColumn={selectedColumn}
                        handleProductSelection={handleProductSelection}
                        decimalScale={2}
                        numberFormatOptions={{
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        }}
                        disabled={submitLoading}
                      />
                    </td>
                    {language !== LanguageFilterType.EN && (
                      <>
                        <td
                          width="30px"
                          onClick={() =>
                            productColumnSelectHandler(
                              product,
                              ProductColumnToUpdate.deStock,
                            )
                          }
                          className={cx(
                            classes.numberColumn,
                            isProductColumnSelected(
                              product,
                              ProductColumnToUpdate.deStock,
                            )
                              ? classes.numberColumnSelected
                              : "",
                            rowClass,
                          )}
                        >
                          <ProductNumberColumn
                            isEditMode={isEditMode}
                            product={product}
                            productField="deStock"
                            productColumn={ProductColumnToUpdate.deStock}
                            rowIndex={rowIndex}
                            selectedRow={selectedRow}
                            selectedColumn={selectedColumn}
                            handleProductSelection={handleProductSelection}
                            decimalScale={0}
                            disabled={submitLoading}
                          />
                        </td>
                        <td
                          width="30px"
                          onClick={() =>
                            productColumnSelectHandler(
                              product,
                              ProductColumnToUpdate.deTransfer,
                            )
                          }
                          className={cx(
                            classes.numberColumn,
                            isProductColumnSelected(
                              product,
                              ProductColumnToUpdate.deTransfer,
                            )
                              ? classes.numberColumnSelected
                              : "",
                            rowClass,
                          )}
                        >
                          <ProductNumberColumn
                            isEditMode={isEditMode}
                            product={product}
                            productField="deTransfer"
                            productColumn={ProductColumnToUpdate.deTransfer}
                            rowIndex={rowIndex}
                            selectedRow={selectedRow}
                            selectedColumn={selectedColumn}
                            handleProductSelection={handleProductSelection}
                            decimalScale={0}
                            disabled={submitLoading}
                          />
                        </td>
                      </>
                    )}
                    {language !== LanguageFilterType.DE && (
                      <>
                        <td
                          width="30px"
                          onClick={() =>
                            productColumnSelectHandler(
                              product,
                              ProductColumnToUpdate.enStock,
                            )
                          }
                          className={cx(
                            classes.numberColumn,
                            isProductColumnSelected(
                              product,
                              ProductColumnToUpdate.enStock,
                            )
                              ? classes.numberColumnSelected
                              : "",
                            rowClass,
                          )}
                        >
                          <ProductNumberColumn
                            isEditMode={isEditMode}
                            product={product}
                            productField="enStock"
                            productColumn={ProductColumnToUpdate.enStock}
                            rowIndex={rowIndex}
                            selectedRow={selectedRow}
                            selectedColumn={selectedColumn}
                            handleProductSelection={handleProductSelection}
                            decimalScale={0}
                            disabled={submitLoading}
                          />
                        </td>
                        <td
                          width="30px"
                          onClick={() =>
                            productColumnSelectHandler(
                              product,
                              ProductColumnToUpdate.enTransfer,
                            )
                          }
                          className={cx(
                            classes.numberColumn,
                            isProductColumnSelected(
                              product,
                              ProductColumnToUpdate.enTransfer,
                            )
                              ? classes.numberColumnSelected
                              : "",
                            rowClass,
                          )}
                        >
                          <ProductNumberColumn
                            isEditMode={isEditMode}
                            product={product}
                            productField="enTransfer"
                            productColumn={ProductColumnToUpdate.enTransfer}
                            rowIndex={rowIndex}
                            selectedRow={selectedRow}
                            selectedColumn={selectedColumn}
                            handleProductSelection={handleProductSelection}
                            decimalScale={0}
                            disabled={submitLoading}
                          />
                        </td>
                      </>
                    )}
                    {language !== LanguageFilterType.EN && (
                      <td width="30px">{product.stockCountDe}</td>
                    )}
                    {language !== LanguageFilterType.DE && (
                      <td width="30px">{product.stockCountEn}</td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </CustomTable>
      </BoxHeadlineContainer>

      {isEditMode && (
        <Grid
          container
          direction="row"
          justifyContent="center"
          spacing={3}
          style={{ marginTop: 33 }}
        >
          <Grid>
            <CustomButton
              text="Änderungen speichern"
              size="small"
              style="filled"
              onClick={handleEditModeSubmit}
              loading={submitLoading}
              disabled={submitLoading}
            />
          </Grid>
          <Grid>
            <CustomButton
              text="Abbrechen"
              size="small"
              style="outlined"
              color="red"
              onClick={() => {
                loadProductInputs();
                resetSelection();
                setIsEditMode(false);
              }}
              loading={submitLoading}
              disabled={submitLoading}
              marginRight={15}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default ProductListPage;
